import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

import { useOdooSearch } from '@/hooks/integrations/use-odoo-search';
import type { OdooResourceType, OdooSearchItem } from '@/types/odoo.types';
import { isOdooProductItem } from '@/types/odoo.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooSearchAutocompleteProps {
  /** The Odoo entity type to search for. */
  resourceType: OdooResourceType;
  /** Callback when a resource is selected. */
  onSelect: (item: OdooSearchItem | null) => void;
  /** Label displayed on the text field. */
  label?: string;
  /** Whether the autocomplete is disabled. */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Render the display label for an Odoo search result. */
function getOptionLabel(option: OdooSearchItem): string {
  if (isOdooProductItem(option)) {
    const code = option.default_code ? ` (${option.default_code})` : '';
    return `${option.name}${code} — ID: ${option.id}`;
  }
  return `${option.name} — ID: ${option.id}`;
}

/** Render secondary info for each option. */
function getSecondaryText(option: OdooSearchItem): string {
  if (isOdooProductItem(option)) {
    return `SKU: ${option.default_code || '—'}`;
  }
  // Partner item — show email and/or VAT if available
  const email = typeof option.email === 'string' ? option.email : null;
  const vat = typeof option.vat === 'string' ? option.vat : null;
  return [email, vat].filter(Boolean).join(' | ') || '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooSearchAutocomplete({
  resourceType,
  onSelect,
  label,
  disabled = false,
}: OdooSearchAutocompleteProps) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<OdooSearchItem | null>(null);

  const { searchTerm, setSearchTerm, results, isFetching } = useOdooSearch({
    resourceType,
    enabled: !disabled,
  });

  const displayLabel = label ?? t('Search in Odoo');

  return (
    <Autocomplete<OdooSearchItem, false, false, false>
      value={selectedValue}
      options={results}
      loading={isFetching}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionDisabled={(option) => option.isLinked === true}
      filterOptions={(x) => x} // Filtering is done server-side
      noOptionsText={
        searchTerm.length < 2 ? t('Type at least 2 characters to search') : t('No results found')
      }
      onChange={(_event, value) => {
        setSelectedValue(value);
        onSelect(value);
      }}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') {
          setSearchTerm(value);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={displayLabel}
          placeholder={t('Type to search...')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {option.name}
              </Typography>
              {option.isLinked && (
                <Chip
                  label={t('Linked')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {`Odoo ID: ${option.id}`}
              {getSecondaryText(option) && ` — ${getSecondaryText(option)}`}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
}
